/// <amd-module name="Build/Admin/Pool/Actions/PoolActionCreator" />
import { caseInsensitiveContains } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

import { IIdentity } from "VSSUI/IdentityPicker";

import { AgentDefinition } from "PoolProvider/Shared/Contracts/PoolProviderShared";

import {
    ElasticNode,
    ElasticNodeState,
    ElasticPool,
    ElasticPoolLog,
    TaskAgentPool,
    TaskAgentQueue,
    TaskAgent,
    TaskAgentJobRequest,
    TaskAgentPoolMaintenanceDefinition,
    TaskAgentPoolMaintenanceJob,
    OperatingSystemType,
    ElasticPoolState,
    ElasticPoolSettings
} from "DistributedTask/Client/Contracts/TaskAgent";

import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

import { ConfirmationDialogActionCreator } from "Build/Admin/Common/Actions/ConfirmationDialogActionCreator";
import { NavigationActionCreator } from "Build/Admin/Common/Actions/NavigationActionCreator";
import { NotificationActionCreator } from "Build/Admin/Common/Actions/NotificationActionCreator";
import { ProjectActionCreator } from "Build/Admin/Common/Actions/ProjectActionCreator";
import { ResourceLimitActionCreator } from "Build/Admin/Common/Actions/ResourceLimitActionCreator";
import { ServiceEndpointActionCreator } from "Build/Admin/Common/Actions/ServiceEndpointActionCreator";
import { VirtualMachineScaleSet } from "Build/Admin/Common/Client/Contracts/DistributedTask/Azure";
import { ElasticPoolData } from "Build/Admin/Common/Client/Contracts/DistributedTask/DataProvider";
import { PoolHubViewKeys, Permission, Telemetry, StoreStatus } from "Build/Admin/Common/Constants";
import { getVMScaleSetOSType } from "Build/Admin/Common/ElasticPoolUtils";
import { IProjectDataService } from "Build/Admin/Common/Services/ProjectDataService";
import { AgentDefinitionSource } from "Build/Admin/Common/Sources/AgentDefinitionSource";
import { AgentPoolSource } from "Build/Admin/Common/Sources/AgentPoolSource";
import { AgentQueueSource } from "Build/Admin/Common/Sources/AgentQueueSource";
import { JobSource } from "Build/Admin/Common/Sources/JobSource";
import { SecuritySource } from "Build/Admin/Common/Sources/SecuritySource";
import { ServiceEndpointSource } from "Build/Admin/Common/Sources/ServiceEndpointSource";
import { NotificationType } from "Build/Admin/Common/Stores/NotificationStore";
import { isElastic, showPoolAgents } from "Build/Admin/Common/TaskAgentPoolUtils";
import { ActionsHub } from "Build/Admin/Pool/Actions/ActionsHub";
import { AgentSource } from "Build/Admin/Pool/Sources/AgentSource";
import { ElasticPoolSource } from "Build/Admin/Pool/Sources/ElasticPoolSource";
import { StoresHub } from "Build/Admin/Pool/Stores/StoresHub";

import * as Resources from "Build/Admin/Pool/Resources";

const defaultTab: string = PoolHubViewKeys.Jobs;

export interface IPoolActionCreatorOptions {
    pageContext: IVssPageContext;
    actionsHub: ActionsHub;
    agentPoolSource: AgentPoolSource;
    agentQueueSource: AgentQueueSource;
    agentSource: AgentSource;
    agentDefinitionSource: AgentDefinitionSource;
    elasticPoolSource: ElasticPoolSource;
    jobSource: JobSource;
    securitySource: SecuritySource;
    serviceEndpointSource: ServiceEndpointSource;
    storesHub: StoresHub;
}

export class PoolActionCreator {
    public confirmationDialogActionCreator: ConfirmationDialogActionCreator<TaskAgent | TaskAgentQueue | TaskAgentPool | ElasticNode>;
    public navigationActionCreator: NavigationActionCreator;
    public projectActionCreator: ProjectActionCreator;
    public resourceLimitActionCreator: ResourceLimitActionCreator;
    public notificationActionCreator: NotificationActionCreator;
    public serviceEndpointActionCreator: ServiceEndpointActionCreator;

    private _actionsHub: ActionsHub;
    private _agentPoolSource: AgentPoolSource;
    private _agentQueueSource: AgentQueueSource;
    private _agentSource: AgentSource;
    private _agentDefinitionSource: AgentDefinitionSource;
    private _elasticPoolSource: ElasticPoolSource;
    private _jobSource: JobSource;
    private _securitySource: SecuritySource;
    private _serviceEndpointSource: ServiceEndpointSource;
    private _storesHub: StoresHub;
    private _pageContext: IVssPageContext;

    constructor(options: IPoolActionCreatorOptions) {
        this._actionsHub = options.actionsHub;
        this._agentPoolSource = options.agentPoolSource;
        this._agentQueueSource = options.agentQueueSource;
        this._agentSource = options.agentSource;
        this._agentDefinitionSource = options.agentDefinitionSource;
        this._elasticPoolSource = options.elasticPoolSource;
        this._jobSource = options.jobSource;
        this._securitySource = options.securitySource;
        this._serviceEndpointSource = options.serviceEndpointSource;
        this._storesHub = options.storesHub;
        this._pageContext = options.pageContext;

        this.confirmationDialogActionCreator = new ConfirmationDialogActionCreator<TaskAgent | TaskAgentQueue | TaskAgentPool | ElasticNode>({ confirmationDialogActions: this._actionsHub.confirmationDialogActions });
        this.navigationActionCreator = new NavigationActionCreator({ pageContext: options.pageContext });
        this.notificationActionCreator = new NotificationActionCreator({ notificationActions: this._actionsHub.notificationActions });
        this.projectActionCreator = new ProjectActionCreator({ pageContext: options.pageContext, projectActions: this._actionsHub.projectActions });
        this.resourceLimitActionCreator = new ResourceLimitActionCreator({
            pageContext: options.pageContext,
            agentPoolSource: this._agentPoolSource,
            resourceLimitActions: this._actionsHub.resourceLimitActions,
            resourceLimitStore: this._storesHub.resourceLimitStore
        });
        this.serviceEndpointActionCreator = new ServiceEndpointActionCreator({
            pageContext: options.pageContext,
            notificationActionCreator: this.notificationActionCreator,
            serviceEndpointActions: this._actionsHub.serviceEndpointActions,
            serviceEndpointSource: this._serviceEndpointSource,
            serviceEndpointStore: this._storesHub.serviceEndpointStore
        });
    }

    public dispose(): void {
        this.projectActionCreator.dispose();
    }

    public initializePool(): void {
        const selectedAgentQueue: TaskAgentQueue | undefined = this._agentQueueSource.getCachedSelectedAgentQueue();
        const selectedAgentPool: TaskAgentPool | undefined = this._agentPoolSource.getCachedSelectedAgentPool();
        const elasticPoolData: ElasticPoolData | undefined = this._elasticPoolSource.getCachedSelectedElasticPoolData();
        let tabIdParam: string | undefined = this.navigationActionCreator.getTabIdParam();

        if (selectedAgentPool) {
            this._actionsHub.selectedAgentQueueUpdated.invoke(selectedAgentQueue);
            this._actionsHub.selectedAgentPoolUpdated.invoke(selectedAgentPool);

            if (elasticPoolData) {
                this._actionsHub.selectedElasticPoolUpdated.invoke(elasticPoolData.elasticPool);
                this._actionsHub.selectedServiceEndpointUpdated.invoke(elasticPoolData.serviceEndpoint);
                this._actionsHub.selectedVirtualMachineScaleSetUpdated.invoke(elasticPoolData.virtualMachineScaleSet);

                if (elasticPoolData.elasticPool && elasticPoolData.elasticPool.state === ElasticPoolState.Offline) {
                    // Set an error banner to alert the user that we are unable to connect to their Elastic Pool resources
                    this.notificationActionCreator.addNotification(
                        Resources.ElasticPoolOffline,
                        NotificationType.error,
                        [{
                            text: Resources.Settings,
                            onClick: (): void => this.updateSelectedTab(PoolHubViewKeys.Settings)
                        }]
                    );
                }
            }

            if (tabIdParam && this._validateTabIdParam(tabIdParam, selectedAgentPool)) {
                this._actionsHub.selectedTabUpdated.invoke(tabIdParam);
            }
            else {
                tabIdParam = defaultTab;
                this._actionsHub.selectedTabUpdated.invoke(defaultTab);
            }

            const queueId: number | undefined = selectedAgentQueue ? selectedAgentQueue.id : undefined;
            this.navigationActionCreator.replaceQueryParameters(queueId, selectedAgentPool.id, tabIdParam, undefined);

            // Initialized data provider data
            this.projectActionCreator.initializeProjects();
            const jobs: TaskAgentJobRequest[] = this._jobSource.getCachedJobs();
            this._actionsHub.jobActions.jobsUpdated.invoke(jobs);
            const agents: TaskAgent[] = this._agentPoolSource.getCachedAgentPoolAgents();
            this._actionsHub.agentsUpdated.invoke(agents);
            const agentDefinitions: AgentDefinition[] = this._agentDefinitionSource.getCachedAgentDefinitions();
            this._actionsHub.agentDefinitionsUpdated.invoke(agentDefinitions);
            this._fetchSecurityData(selectedAgentPool.id, selectedAgentQueue ? selectedAgentQueue.id : undefined);

            this._fetchTabData(tabIdParam, selectedAgentPool.id, selectedAgentQueue ? selectedAgentQueue.id : selectedAgentPool.id);
        }
    }

    public dismissDownloadAgentDialog(): void {
        this._actionsHub.dismissDownloadAgentDialog.invoke();
    }

    public showDownloadAgentDialog(): void {
        this._actionsHub.showDownloadAgentDialog.invoke();
    }

    public updateSelectedTab(newTabId: string): void {
        const selectedAgentPool = this._storesHub.selectionStore.state.selectedAgentPool;
        const selectedAgentQueue = this._storesHub.selectionStore.state.selectedAgentQueue;

        if (selectedAgentPool
            && this._validateTabIdParam(newTabId, this._storesHub.selectionStore.state.selectedAgentPool)
            && newTabId !== this._storesHub.selectionStore.state.selectedTabId) {
            this.navigationActionCreator.setTabIdParam(newTabId);
            this._fetchTabData(newTabId, selectedAgentPool.id, selectedAgentQueue ? selectedAgentQueue.id : selectedAgentPool.id);
            this._actionsHub.selectedTabUpdated.invoke(newTabId);
        }
    }

    public enableAgent(agent: TaskAgent, poolId: number, enabled: boolean): void {
        const agentToUpdate: TaskAgent = { ...agent, enabled };

        // Speculatively update the store so the UI feedback doesn't lag
        this._actionsHub.agentActions.agentUpdated.invoke(agentToUpdate);

        // Now make the network request
        this._agentSource.updateAgent({ id: agent.id, enabled } as TaskAgent, poolId).then(
            (updatedAgent: TaskAgent) => {
                // Go ahead and update again, in case there's anything else new on the agent object
                this._actionsHub.agentActions.agentUpdated.invoke(updatedAgent);
            },
            (reason) => {
                // "Roll back" the update to the store
                this._actionsHub.agentActions.agentUpdated.invoke(agent);
                this._handleError(reason);
            }
        );
    }

    public deleteAgent(agent: TaskAgent, poolId: number): void {
        this._agentSource.deleteAgent(agent, poolId).then(
            () => {
                this._actionsHub.agentDeleted.invoke(agent);
            },
            (reason) => {
                this._handleError(reason);
            }
        );
    }

    public getAgentData(agentId: number, poolId: number): void {
        this._agentSource.getAgent(agentId, poolId).then(
            (agent) => {
                this._actionsHub.agentActions.agentUpdated.invoke(agent);
            },
            (reason) => {
                this._handleError(reason);
            }
        );
    }

    public getRequestData(poolId: number, requestId: number): void {
        this._jobSource.getAgentRequest(poolId, requestId, true).then(
            (agentRequest) => {
                this._actionsHub.jobActions.jobUpdated.invoke(agentRequest);
            },
            (reason) => {
                this._handleError(reason);
            }
        );
    }

    public pushAllAgentsUpdateRequest(poolId: number): void {
        this._agentPoolSource.refreshAgents(poolId).catch(
            (reason) => {
                this._handleError(reason);
            }
        );
    }

    public pushAgentUpdateRequest(poolId: number, agent: TaskAgent): void {
        this._agentSource.refreshAgent(agent, poolId).catch(
            (reason) => {
                this._handleError(reason);
            }
        );
    }

    public setPipelinesAuthorizedForQueue(agentQueue: TaskAgentQueue, authorizeAllPipelines: boolean): void {
        const authorizationState: boolean = this._storesHub.securityStore.state.pipelinesAuthorizedForQueue;

        // Speculatively update the store so the UI feedback doesn't lag
        this._actionsHub.agentQueuePipelinesAuthorizedUpdated.invoke(authorizeAllPipelines);

        this._securitySource.setPipelinesAuthorizedForQueue(agentQueue, authorizeAllPipelines).then(
            (pipelinesAuthorized: boolean) => {
                // Update with new state in case of other server side changes
                this._actionsHub.agentQueuePipelinesAuthorizedUpdated.invoke(pipelinesAuthorized);
            },
            (reason) => {
                // "Roll back" the update to the store
                this._actionsHub.agentQueuePipelinesAuthorizedUpdated.invoke(authorizationState);
                this._handleError(reason);
            }
        );
    }

    public setPoolAutoProvisioning(autoProvision: boolean): void {
       const selectedPool: TaskAgentPool | undefined = this._storesHub.selectionStore.state.selectedAgentPool;

       if (selectedPool) {
           const updatedPool: TaskAgentPool = { ...selectedPool, autoProvision };

           // Speculatively update the store so the UI feedback doesn't lag
           this._actionsHub.selectedAgentPoolUpdated.invoke(updatedPool);

           // Only send the properties we actually need over the wire
           this._agentPoolSource.updateAgentPool({ id: selectedPool.id, autoProvision } as TaskAgentPool).then(
               (newPool: TaskAgentPool) => {
                    // Update with new state in case of other server side changes
                    this._actionsHub.selectedAgentPoolUpdated.invoke(newPool);
               },
               (reason) => {
                    // "Roll back" the update to the store
                    this._actionsHub.selectedAgentPoolUpdated.invoke(selectedPool);
                    this._handleError(reason);
               }
           );
       }
    }

    public setPoolAutoUpdate(autoUpdate: boolean): void {
        const selectedPool: TaskAgentPool | undefined = this._storesHub.selectionStore.state.selectedAgentPool;

        if (selectedPool) {
            const updatedPool: TaskAgentPool = { ...selectedPool, autoUpdate };

            // Speculatively update the store so the UI feedback doesn't lag
            this._actionsHub.selectedAgentPoolUpdated.invoke(updatedPool);

            // Only send the properties we actually need over the wire
            this._agentPoolSource.updateAgentPool({ id: selectedPool.id, autoUpdate } as TaskAgentPool).then(
                (newPool: TaskAgentPool) => {
                     // Update with new state in case of other server side changes
                     this._actionsHub.selectedAgentPoolUpdated.invoke(newPool);
                },
                (reason) => {
                     // "Roll back" the update to the store
                     this._actionsHub.selectedAgentPoolUpdated.invoke(selectedPool);
                     this._handleError(reason);
                }
            );
        }
     }

    public setPoolOwner(identity: IIdentity): void {
        const selectedPool: TaskAgentPool | undefined = this._storesHub.selectionStore.state.selectedAgentPool;

        if (selectedPool && identity.subjectDescriptor) {
            const owner: IdentityRef = {
                displayName: identity.displayName ? identity.displayName : "",
                descriptor: identity.subjectDescriptor,
                imageUrl: identity.image ? identity.image : ""
            } as IdentityRef;

            const updatedPool: TaskAgentPool = {
                ...selectedPool,
                owner
            };

            // Speculatively update the store so the UI feedback doesn't lag
            this._actionsHub.selectedAgentPoolUpdated.invoke(updatedPool);

            // Only send the properties we actually need over the wire
            this._agentPoolSource.updateAgentPool({ id: selectedPool.id, owner } as TaskAgentPool).then(
                (newPool: TaskAgentPool) => {
                    // Update with new state in case of other server side changes
                    this._actionsHub.selectedAgentPoolUpdated.invoke(newPool);
                },
                (reason) => {
                    // "Roll back" the update to the store
                    this._actionsHub.selectedAgentPoolUpdated.invoke(selectedPool);
                    this._handleError(reason);
                }
            );
        }
    }

    public setAgentPoolDescription(pool: TaskAgentPool, agentPoolDescription: string): void {
        if (this._storesHub.descriptionStore.state.status === StoreStatus.Loaded) {
            const currentMetadata: string = this._storesHub.descriptionStore.state.description;

            // Speculatively update the store so the UI feedback doesn't lag
            this._actionsHub.agentPoolDescriptionUpdated.invoke(agentPoolDescription);

            // Only send the properties we actually need over the wire
            this._agentPoolSource.setAgentPoolMetadata(pool.id, agentPoolDescription).then(
                undefined,
                (reason) => {
                    // "Roll back" the update to the store
                    this._actionsHub.agentPoolDescriptionUpdated.invoke(currentMetadata);
                    this._handleError(reason);
                }
            )
        }
     }

    public createOrUpdateMaintenanceDefinition(definition: TaskAgentPoolMaintenanceDefinition, poolId: number): void {
        if (definition.id <= 0) {
            this._agentPoolSource.createMaintenanceDefinition(definition, poolId).then(
                (newDefinition) => {
                    this._actionsHub.maintenanceDefinitionAdded.invoke(newDefinition);
                },
                (reason) => {
                    this._handleError(reason);
                }
            );
        }
        else {
            this._agentPoolSource.updateMaintenanceDefinition(definition, poolId).then(
                (updatedDefinition) => {
                    this._actionsHub.maintenanceDefinitionUpdated.invoke(updatedDefinition);
                },
                (reason) => {
                    this._handleError(reason);
                }
            );
        }
    }

    public updateElasticPoolSettings(recycleAfterEachUse: boolean, maxSavedNodeCount: number, maxCapacity: number, desiredIdle: number, timeToLiveMinutes: number, agentInteractiveUI: boolean): void {
        const existingElasticPool: ElasticPool | undefined = this._storesHub.selectionStore.state.selectedElasticPool;
        if (existingElasticPool) {
            const updatedElasticPool: ElasticPool = {
                ...existingElasticPool,
                recycleAfterEachUse,
                maxSavedNodeCount,
                maxCapacity,
                desiredIdle,
                timeToLiveMinutes,
                agentInteractiveUI
            };

            // Speculatively update the store so the UI feedback doesn't lag
            this._actionsHub.selectedElasticPoolUpdated.invoke(updatedElasticPool);

            // Only send the properties we actually need over the wire
            this._elasticPoolSource.updateElasticPoolAsync(
                existingElasticPool.poolId,
                {
                    recycleAfterEachUse,
                    maxSavedNodeCount,
                    maxCapacity,
                    desiredIdle,
                    timeToLiveMinutes,
                    agentInteractiveUI
                } as ElasticPoolSettings).then(
                (newElasticPool: ElasticPool) => {
                    // Update with new state in case of other server side changes
                    this._actionsHub.selectedElasticPoolUpdated.invoke(newElasticPool);
                },
                (reason) => {
                    // "Roll back" the update to the store
                    this._actionsHub.selectedElasticPoolUpdated.invoke(existingElasticPool);
                    this._handleError(reason);
                }
            );
        }
    }

    public updateElasticPoolAzureSubscriptionSettings(projectId: string, serviceEndpoint: ServiceEndpoint, virtualMachineScaleSet: VirtualMachineScaleSet): void {
        const existingElasticPool: ElasticPool | undefined = this._storesHub.selectionStore.state.selectedElasticPool;
        const existingServiceEndpoint: ServiceEndpoint | undefined = this._storesHub.selectionStore.state.selectedServiceEndpoint;
        const existingVirtualMachineScaleSet: VirtualMachineScaleSet | undefined = this._storesHub.selectionStore.state.selectedVirtualMachineScaleSet;

        if (existingElasticPool) {
            const osType: OperatingSystemType | undefined = getVMScaleSetOSType(virtualMachineScaleSet);

            const updatedElasticPool: ElasticPool = {
                ...existingElasticPool,
                serviceEndpointId: serviceEndpoint.id,
                serviceEndpointScope: projectId,
                azureId: virtualMachineScaleSet.id,
                osType
            } as ElasticPool;

            // Speculatively update the store so the UI feedback doesn't lag
            this._actionsHub.selectedElasticPoolUpdated.invoke(updatedElasticPool);
            this._actionsHub.selectedServiceEndpointUpdated.invoke(serviceEndpoint);
            this._actionsHub.selectedVirtualMachineScaleSetUpdated.invoke(virtualMachineScaleSet);

            // Only send the properties we actually need over the wire
            this._elasticPoolSource.updateElasticPoolAsync(
                existingElasticPool.poolId,
                {
                    serviceEndpointId: serviceEndpoint.id,
                    serviceEndpointScope: projectId,
                    azureId: virtualMachineScaleSet.id,
                    osType
                } as ElasticPoolSettings).then(
                (newElasticPool: ElasticPool) => {
                    // Update with new state in case of other server side changes
                    this._actionsHub.selectedElasticPoolUpdated.invoke(newElasticPool);
                    // If the service connection wasn't updated properly, roll back
                    if (newElasticPool.serviceEndpointId === existingServiceEndpoint?.id) {
                        this._actionsHub.selectedServiceEndpointUpdated.invoke(existingServiceEndpoint);
                    }
                    // If the vmss wasn't updated properly, roll back
                    if (newElasticPool.azureId === existingVirtualMachineScaleSet?.id) {
                        this._actionsHub.selectedVirtualMachineScaleSetUpdated.invoke(existingVirtualMachineScaleSet);
                    }
                },
                (reason) => {
                    // "Roll back" the update to the store
                    this._actionsHub.selectedElasticPoolUpdated.invoke(existingElasticPool);
                    this._actionsHub.selectedServiceEndpointUpdated.invoke(existingServiceEndpoint);
                    this._actionsHub.selectedVirtualMachineScaleSetUpdated.invoke(existingVirtualMachineScaleSet);
                    this._handleError(reason);
                }
            );
        }
    }

    public deleteElasticNode(elasticNode: ElasticNode): void {
        const existingElasticNode: ElasticNode | undefined = this._storesHub.elasticNodeStore.state.elasticNodes.filter(n => n.id === elasticNode.id)[0];
        if (existingElasticNode && existingElasticNode.state === ElasticNodeState.Saved) {
            // To release an elastic node from the saved state set the state to pending delete
            const updatedElasticNode: ElasticNode = {
                ...existingElasticNode,
                state: ElasticNodeState.PendingDelete
            };

            // Speculatively update the store so the UI feedback doesn't lag
            this._actionsHub.elasticNodeUpdated.invoke(updatedElasticNode);

            this._elasticPoolSource.updateElasticNodeAsync(elasticNode.poolId, elasticNode.id, { state: ElasticNodeState.PendingDelete }).then(
                (newElasticNode: ElasticNode) => {
                    this._actionsHub.elasticNodeUpdated.invoke(newElasticNode);
                },
                (reason) => {
                    // "Roll back" the update to the store
                    this._actionsHub.elasticNodeUpdated.invoke(existingElasticNode);
                    this._handleError(reason);
                }
            )
        }
    }

    private _fetchTabData(tabId: string, agentPoolId: number, agentQueueId: number | undefined): void {
        if (agentPoolId) {
            switch (tabId) {
                case PoolHubViewKeys.Details:
                    this._fetchAgentPoolDescription(agentPoolId);
                    break;
                case PoolHubViewKeys.Diagnostics:
                    this._fetchPoolDiagnostics(agentPoolId);
                    // Fetch saved elastic nodes for the diagnostics tab
                    this._fetchElasticNodes(agentPoolId, ElasticNodeState.Saved);
                    break;
                case PoolHubViewKeys.MaintenanceHistory:
                    this._fetchMaintenanceJobs(agentPoolId);
                    break;
                case PoolHubViewKeys.Security:
                    break;
                case PoolHubViewKeys.Settings:
                    this._actionsHub.maintenanceTimeZonesUpdated.invoke(this._agentPoolSource.getCachedValidTimezones());
                    this._fetchMaintenanceDefinitions(agentPoolId);
                    this.resourceLimitActionCreator.fetchResourceLimits();
                    break;
                case PoolHubViewKeys.Agents:
                case PoolHubViewKeys.Jobs:
                case PoolHubViewKeys.Checks:
                    if (agentQueueId) {
                        this._fetchCheckData(agentQueueId);
                    }
                default:
                    return;
            }
        }
    }

    private _fetchAgentPoolDescription(taskAgentPoolId: number): void {
        if (this._storesHub.descriptionStore.state.status === StoreStatus.None) {
            this._actionsHub.descriptionStoreStatusUpdated.invoke(StoreStatus.Loading);

            this._agentPoolSource.getAgentPoolMetadata(taskAgentPoolId).then(
                (markdownMetadataDocument: string) => {
                    this._actionsHub.agentPoolDescriptionUpdated.invoke(markdownMetadataDocument);
                },
                (reason) => {
                    if (reason && reason.message) {
                        // 404s and not found errors are expected when there is no metadata, other errors should bubble up
                        // Currently, when DT calls to MMS for hosted pool metadata, the 404 from MMS gets wrapped in a 500
                        // from DT and sent to us. I am checking the message below for "not found" to account for this.
                        // There is likely a bug that can be fixed service side to ensure that 404 doesn't turn into a 500.
                        if (!(reason.status && reason.status === 404) && !caseInsensitiveContains(reason.message, "not found")) {
                            this._handleError(reason);
                        }
                    }

                    this._actionsHub.agentPoolDescriptionUpdated.invoke("");
                }
            );
        }
    }

    private _fetchSecurityData(agentPoolId: number, agentQueueId: number | undefined): void {
        if (this._storesHub.securityStore.state.status === StoreStatus.None) {
            this._actionsHub.securityStoreStatusUpdated.invoke(StoreStatus.Loading);
            const projectDataService = this._pageContext.getService<IProjectDataService>("IProjectDataService");

            if (projectDataService.hasProjectData() && agentQueueId) {
                const hasAgentQueueManagePermission: boolean = this._securitySource.hasAgentQueuePermission(agentQueueId, Permission.Manage);
                this._actionsHub.manageQueuePermissionUpdated.invoke(hasAgentQueueManagePermission);

                const hasAgentPoolManagePermission: boolean = this._securitySource.hasAgentPoolPermission(agentPoolId, Permission.Manage);
                this._actionsHub.managePoolPermissionUpdated.invoke(hasAgentPoolManagePermission);

                const hasAllAgentQueuesManagePermission: boolean = this._securitySource.hasAllAgentQueuesPermission(Permission.Manage);
                this._actionsHub.manageAllQueuesPermissionUpdated.invoke(hasAllAgentQueuesManagePermission);

                const hasAllAgentPoolsManagePermission: boolean = this._securitySource.hasAllAgentPoolsPermission(Permission.Manage);
                this._actionsHub.manageAllPoolsPermissionUpdated.invoke(hasAllAgentPoolsManagePermission);

                this._securitySource.getPipelinesAuthorizedForQueue(agentQueueId).then(
                    (pipelinesAuthorized: boolean) => {
                        this._actionsHub.agentQueuePipelinesAuthorizedUpdated.invoke(pipelinesAuthorized);
                    },
                    (reason) => {
                        this._handleError(reason);
                    }
                );
            }
            else {
                const hasAgentPoolManagePermission: boolean = this._securitySource.hasAgentPoolPermission(agentPoolId, Permission.Manage);
                this._actionsHub.managePoolPermissionUpdated.invoke(hasAgentPoolManagePermission);
                this._actionsHub.agentQueuePipelinesAuthorizedUpdated.invoke(false); // This setting doesn't apply at the pool (org) level
            }
        }
    }

    private _fetchMaintenanceDefinitions(poolId: number): void {
        if (this._storesHub.maintenanceStore.state.status === StoreStatus.None) {
            this._actionsHub.maintenanceStoreStatusUpdated.invoke(StoreStatus.Loading);

            this._agentPoolSource.getMaintenanceDefinitions(poolId).then(
                (definitions: TaskAgentPoolMaintenanceDefinition[]) => {
                    this._actionsHub.maintenanceDefinitionsUpdated.invoke(definitions);
                },
                (reason) => {
                    this._handleError(reason);
                    this._actionsHub.maintenanceDefinitionsUpdated.invoke([]);
                }
            );
        }
    }

    private _fetchMaintenanceJobs(poolId: number): void {
        if (this._storesHub.maintenanceJobStore.state.status === StoreStatus.None) {
            this._actionsHub.maintenanceJobsStoreStatusUpdated.invoke(StoreStatus.Loading);

            this._jobSource.getAgentPoolMaintenanceJobs(poolId).then(
                (jobs: TaskAgentPoolMaintenanceJob[]) => {
                    this._actionsHub.maintenanceJobsUpdated.invoke(jobs);
                },
                (reason) => {
                    this._handleError(reason);
                    this._actionsHub.maintenanceJobsUpdated.invoke([]);
                }
            )
        }
    }

    private _fetchPoolDiagnostics(poolId: number): void {
        if (this._storesHub.elasticPoolLogStore.state.status === StoreStatus.None) {
            this._actionsHub.elasticPoolLogStoreStatusUpdated.invoke(StoreStatus.Loading);

            this._elasticPoolSource.getElasticPoolLogsAsync({ poolId: poolId } as ElasticPool).then(
                (logs: ElasticPoolLog[]) => {
                    this._actionsHub.elasticPoolLogsUpdated.invoke(logs);
                },
                (reason) => {
                    this._handleError(reason);
                    this._actionsHub.elasticPoolLogsUpdated.invoke([]);
                }
            )
        }
    }

    private _fetchElasticNodes(poolId: number, state?: ElasticNodeState | undefined): void {
        if (this._storesHub.elasticNodeStore.state.status === StoreStatus.None) {
            this._actionsHub.elasticNodeStoreStatusUpdated.invoke(StoreStatus.Loading);

            this._elasticPoolSource.getElasticNodesAsync({ poolId: poolId } as ElasticPool, state).then(
                (nodes: ElasticNode[]) => {
                    this._actionsHub.elasticNodesUpdated.invoke(nodes);
                },
                (reason) => {
                    this._handleError(reason);
                    this._actionsHub.elasticNodesUpdated.invoke([]);
                }
            )
        }
    }

    private _fetchCheckData(queueId: number): void {
        if (this._storesHub.checksStore.state.status === StoreStatus.None) {
            this._actionsHub.checksStoreStatusUpdated.invoke(StoreStatus.Loading);
            const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService.getDataAsync("ms.vss-pipelinechecks.checks-data-provider", {"queueId": queueId}, true)
                .then(() => this._actionsHub.checksStoreStatusUpdated.invoke(StoreStatus.Loaded))
                .catch(this._handleError);
        }
    }

    private _validateTabIdParam(tabId: string, selectedAgentPool: TaskAgentPool | undefined): boolean {
        if (tabId === PoolHubViewKeys.Agents && !showPoolAgents(selectedAgentPool)) {
            return false;
        }
        else if (tabId === PoolHubViewKeys.Diagnostics && !isElastic(selectedAgentPool)) {
            return false;
        }
        else if (tabId === PoolHubViewKeys.Details ||
            tabId === PoolHubViewKeys.Jobs ||
            tabId === PoolHubViewKeys.Security ||
            tabId === PoolHubViewKeys.Agents ||
            tabId === PoolHubViewKeys.Settings ||
            tabId === PoolHubViewKeys.MaintenanceHistory ||
            tabId === PoolHubViewKeys.Diagnostics ||
            tabId === PoolHubViewKeys.Analytics ||
            tabId === PoolHubViewKeys.Checks) {
            return true;
        }
        else {
            return false;
        }
    }

    // tslint:disable-next-line:no-any
    private _handleError(reason: any): void {
        if (reason && reason.message) {
            this.notificationActionCreator.addNotification(reason.message, NotificationType.error);
            const service = this._pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            service.publishEvent(Telemetry.Area, Telemetry.PoolsAdminFeature, {
                message: reason.message,
                error: reason
            });
        }
    }
}
